import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router'
import { cn } from '@/shared/lib/utils'

export type ButtonVariant = 'primary' | 'accent' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

const variantClass: Record<ButtonVariant, string> = {
  primary: 'bg-navy-900 text-white hover:bg-navy-800 active:bg-navy-950',
  accent: 'bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700',
  secondary:
    'border border-line bg-white text-navy-900 hover:border-neutral-300 hover:bg-neutral-50',
  ghost: 'text-navy-900 hover:bg-neutral-100',
  danger: 'bg-danger-600 text-white hover:bg-danger-700',
}

const sizeClass: Record<ButtonSize, string> = {
  sm: 'min-h-9 gap-1.5 px-3 text-xs',
  md: 'min-h-11 gap-2 px-4 text-sm',
  lg: 'min-h-13 gap-2.5 px-5 text-sm',
}

const base =
  'inline-flex items-center justify-center rounded-xl font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-55'

interface CommonProps {
  variant?: ButtonVariant
  size?: ButtonSize
  fullWidth?: boolean
  loading?: boolean
  className?: string
  children: ReactNode
}

type ButtonProps = CommonProps & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'>

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth,
  loading,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={cn(base, variantClass[variant], sizeClass[size], fullWidth && 'w-full', className)}
      {...rest}
    >
      {loading && <Spinner />}
      {children}
    </button>
  )
}

interface ButtonLinkProps extends CommonProps {
  to: string
  state?: unknown
  ariaLabel?: string
}

export function ButtonLink({
  to,
  state,
  ariaLabel,
  variant = 'primary',
  size = 'md',
  fullWidth,
  className,
  children,
}: ButtonLinkProps) {
  return (
    <Link
      to={to}
      state={state}
      aria-label={ariaLabel}
      className={cn(base, variantClass[variant], sizeClass[size], fullWidth && 'w-full', className)}
    >
      {children}
    </Link>
  )
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent',
        className
      )}
      aria-hidden="true"
    />
  )
}
